"use client";

import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  PackageSearch,
  Search,
  ShoppingBag,
} from "lucide-react";
import { useMemo } from "react";
import { type PublicNamedItem } from "@/lib/supabase/public-data";
import {
  categoryCoversOf,
  categoryTileClass,
  masterLogoMap,
  masterLogoOf,
  type CategoryCount,
  type CategoryCovers,
  type HomeProductType,
} from "@/lib/homeCatalog";
import { categoryIcon } from "@/components/home/categoryIcons";
import { CategoryThumb } from "@/components/home/CategoryThumb";
import { useCart } from "@/lib/cart";
import { NotificationBell } from "@/components/NotificationBell";
import { useCustomerGroup } from "@/context/CustomerContext";
import { CatalogBrowse, type BrowseDims } from "@/components/catalog/CatalogBrowse";
import { AdminListMemory } from "@/components/admin/AdminListMemory";

const HEADER_GRADIENT =
  "bg-gradient-to-r from-shopeeDark via-shopee to-shopeeLight";

const normalize = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

/** Tautan ke halaman kategori (param tunggal untuk landing/link sederhana). */
function kategoriHref(params: {
  cat?: string | null;
  segment?: string | null;
}): string {
  const search = new URLSearchParams();
  if (params.cat) search.set("cat", params.cat);
  if (params.segment) search.set("segment", params.segment);
  const qs = search.toString();
  return qs ? `/kategori?${qs}` : "/kategori";
}

/** URL dari dimensi browse efektif (multi-pilih → param berulang). */
function browseHref(dims: BrowseDims): string {
  const search = new URLSearchParams();
  dims.categories.forEach((c) => search.append("cat", c));
  dims.segments.forEach((s) => search.append("segment", s));
  dims.brands.forEach((b) => search.append("brand", b));
  const qs = search.toString();
  return qs ? `/kategori?${qs}` : "/kategori";
}

/**
 * Halaman Kategori (`/kategori`). Tanpa kategori/segmen → LANDING grid semua
 * kategori + shortcut segmen. Drill-down (kategori/segmen terkunci) → produk
 * SISI-SERVER lewat `CatalogBrowse` BERSAMA. Pola cermin halaman Merek.
 */
export function KategoriClient({
  categoryCounts,
  landingSegments,
  categories,
  segments,
  categoryCovers,
  lockedCategory = null,
  lockedSegment = null,
  seedSegments = [],
  seedBrands = [],
  initialType,
  storeId = null,
}: {
  /** PF-01: agregat landing dihitung server (`kategori-data.ts`), bukan index penuh. */
  categoryCounts: CategoryCount[];
  landingSegments: string[];
  categories: PublicNamedItem[];
  segments: PublicNamedItem[];
  /** Foto kartu grid landing (server, `categoryCoverPhotos`). */
  categoryCovers?: CategoryCovers;
  lockedCategory?: string | null;
  lockedSegment?: string | null;
  seedSegments?: string[];
  seedBrands?: string[];
  initialType?: HomeProductType;
  /** MULTI-TENANT FASE 4: toko hasil resolusi Host (dari server page). */
  storeId?: string | null;
}) {
  const { totalQty } = useCart();
  const group = useCustomerGroup();
  const now = useMemo(() => new Date(), []);

  // Ikon kategori unggahan admin (`product_categories.icon_url`, mig 0951).
  // `categoryCounts` hanya membawa nama+jumlah (PF-01), jadi ikonnya dijodohkan
  // di sini dari master `categories` yang memang sudah dikirim — tanpa request
  // tambahan. Cermin `MerekClient`.
  const categoryLogos = useMemo(() => masterLogoMap(categories), [categories]);
  const categoryTiles = useMemo(
    () =>
      categoryCounts.map((c) => ({
        ...c,
        iconUrl: masterLogoOf(categoryLogos, c.name),
        photos: categoryCoversOf(categoryCovers, c.name),
      })),
    [categoryCounts, categoryLogos, categoryCovers]
  );

  const isDetail = Boolean(lockedCategory) || Boolean(lockedSegment);
  const title = lockedCategory ?? lockedSegment ?? "Kategori";

  // #5: kategori/segmen primer di URL tak dikenal master → "tidak ditemukan".
  const knownCategory =
    !lockedCategory ||
    categories.some((c) => normalize(c.name) === normalize(lockedCategory));
  const knownSegment =
    !lockedSegment ||
    segments.some((s) => normalize(s.name) === normalize(lockedSegment));
  const notFound = isDetail && (!knownCategory || !knownSegment);

  const cartHref = "/keranjang";

  return (
    <div className="min-h-screen bg-neutral-100">
      <header className={`sticky top-0 z-30 ${HEADER_GRADIENT} px-3 py-3 safe-top`}>
        <div className="flex items-center gap-2.5">
          <Link
            href={isDetail ? "/kategori" : "/"}
            aria-label="Kembali"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-white"
          >
            <ChevronLeft size={24} />
          </Link>
          <h1 className="min-w-0 flex-1 truncate text-base font-extrabold text-white">
            {title}
          </h1>
          <div className="flex shrink-0 items-center">
            <Link
              href="/cari"
              aria-label="Cari"
              className="grid h-10 w-10 shrink-0 place-items-center text-white"
            >
              <Search size={22} />
            </Link>
            <NotificationBell variant="bar" />
            <Link
              href={cartHref}
              aria-label="Keranjang"
              className="relative grid h-10 w-10 shrink-0 place-items-center text-white"
            >
              <ShoppingBag size={24} />
              {totalQty > 0 ? (
                <span className="absolute right-0 top-0 grid h-4 min-w-4 place-items-center rounded-full border border-shopee bg-white px-1 text-[9px] font-bold leading-none text-shopee">
                  {totalQty}
                </span>
              ) : null}
            </Link>
          </div>
        </div>
      </header>

      {isDetail && !notFound ? (
        <Breadcrumb crumb={lockedCategory ? "Kategori" : "Segmen"} label={title} />
      ) : null}

      {notFound ? (
        <NotFoundState
          label={!knownCategory ? lockedCategory : lockedSegment}
        />
      ) : isDetail ? (
        // Kembali dari halaman produk → drill, tipe, urutan & posisi daftar
        // dipulihkan (memori daftar, `ssr`: halaman dirender server).
        <AdminListMemory page="kategori" ssr key={`${lockedCategory ?? ""}|${lockedSegment ?? ""}`}>
          <CatalogBrowse
            storeId={storeId}
            lockedCategory={lockedCategory}
            lockedSegment={lockedSegment}
            seedSegments={seedSegments}
            seedBrands={seedBrands}
            initialType={initialType ?? null}
            group={group}
            now={now}
            buildHref={browseHref}
          />
        </AdminListMemory>
      ) : (
        <CategoryLanding categories={categoryTiles} segments={landingSegments} />
      )}
    </div>
  );
}

/** Breadcrumb "Beranda › {crumb} › {label}" untuk halaman detail (#4). */
function Breadcrumb({ crumb, label }: { crumb: string; label: string }) {
  return (
    <nav className="flex items-center gap-1 bg-white px-3 py-1.5 text-[11px] font-semibold text-muted">
      <Link href="/" className="hover:text-shopee">
        Beranda
      </Link>
      <ChevronRight size={12} />
      <Link href="/kategori" className="hover:text-shopee">
        {crumb}
      </Link>
      <ChevronRight size={12} />
      <span className="truncate text-ink">{label}</span>
    </nav>
  );
}

/** Grid semua kategori (landing) + chip shortcut segmen lintas-kategori. */
function CategoryLanding({
  categories,
  segments,
}: {
  categories: Array<{
    name: string;
    count: number;
    iconUrl?: string;
    photos: string[];
  }>;
  segments: string[];
}) {
  if (!categories.length && !segments.length) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
        <PackageSearch size={44} className="text-neutral-300" />
        <p className="text-sm font-bold text-ink">Belum ada kategori</p>
        <Link
          href="/"
          className="grid h-9 place-items-center rounded-full bg-shopee px-5 text-xs font-bold text-white"
        >
          Kembali ke Beranda
        </Link>
      </div>
    );
  }

  return (
    <>
      {segments.length ? (
        <section className="bg-white px-3 py-3">
          <h2 className="mb-2 text-sm font-extrabold text-ink">
            Belanja per Segmen
          </h2>
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
            {segments.map((name) => (
              <Link
                key={name}
                href={kategoriHref({ segment: name })}
                prefetch={false}
                className="h-9 shrink-0 rounded-full border border-shopee bg-shopeeSoft px-4 text-xs font-bold leading-9 text-shopee"
              >
                {name}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="px-3 py-4">
        <h2 className="mb-3 text-sm font-extrabold text-ink">Semua Kategori</h2>
        <div className="grid grid-cols-3 gap-2">
          {categories.map(({ name, count, iconUrl, photos }) => {
            return (
              <Link
                key={name}
                href={kategoriHref({ cat: name })}
                // Grid landing me-render SEMUA kategori — prefetch default =
                // 1 request RSC/tile ke origin (lihat HomeProductCard).
                prefetch={false}
                className="flex flex-col items-center gap-1.5 rounded-xl bg-white px-1 py-3 shadow-sm transition active:scale-[0.98]"
              >
                <CategoryThumb
                  size="landing"
                  photos={photos}
                  iconUrl={iconUrl}
                  icon={categoryIcon(name)}
                  colorClass={categoryTileClass(name)}
                />
                <span className="line-clamp-2 min-h-7 text-center text-[11px] font-bold leading-tight text-ink">
                  {name}
                </span>
                <span className="text-[10px] font-medium text-muted">
                  {count} produk
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}

function NotFoundState({ label }: { label: string | null }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
      <PackageSearch size={44} className="text-neutral-300" />
      <p className="text-sm font-bold text-ink">
        {label ? `"${label}" tidak ditemukan` : "Tidak ditemukan"}
      </p>
      <p className="max-w-xs text-xs text-muted">
        Kategori atau segmen ini mungkin sudah dihapus.
      </p>
      <Link
        href="/kategori"
        className="grid h-9 place-items-center rounded-full bg-shopee px-5 text-xs font-bold text-white"
      >
        Lihat Semua Kategori
      </Link>
    </div>
  );
}
